import {
  configureStore,
  combineReducers,
  Reducer,
  UnknownAction,
  Store,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { baseApi, apiMiddleware } from "./api";
import languageReducer from "../parts/header/language/languageSlice";
import productReducer from "../templates/ecommerce/product/productSlice";

// Define an interface for our extensible store, allowing for code splitting
export interface InjectedStore extends Store {
  asyncReducers?: { [key: string]: Reducer<any, UnknownAction> };
  injectReducer?: (key: string, asyncReducer: Reducer) => void;
}

// Define the reducers that are known at initialization time.
const staticReducers = {
  [baseApi.reducerPath]: baseApi.reducer,
  language: languageReducer,
  product: productReducer,
};

// Create the main store configuration.
export const store: InjectedStore = configureStore({
  reducer: staticReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apiMiddleware),
});

// Create a placeholder for async reducers that might be added later.
store.asyncReducers = {};

// Create the injection function and attach it to the store instance.
// This allows other parts of the app to inject their own reducers as needed.
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers) {
    store.asyncReducers = {};
  }
  store.asyncReducers[key] = asyncReducer;

  // Combine the static and newly injected reducers to create a new root reducer.
  const newRootReducer = combineReducers({
    ...staticReducers,
    ...store.asyncReducers,
  });

  store.replaceReducer(newRootReducer);
};

// Set up listeners for RTK Query to enable caching, invalidation, polling, etc.
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself for TypeScript.
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
